import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Lock, Volume2, VolumeX, Play, Pause } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { debug, error as logError } from '../../utils/logger';
import { DjayNarrator, BrandLockup } from '../../mascot';
interface WelcomeScreenProps {
  // Hier stonden `onLoadDemo` en `isLoadingDemo`. De demo-laag is weg; de
  // props zijn meeverdwenen in plaats van als optionele rest te blijven staan.
  onLogin: () => void;
  onRegister: () => void;
}
export function WelcomeScreen({
  onLogin,
  onRegister
}: WelcomeScreenProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isMuted, setIsMuted] = useState(false); // Start unmuted as requested
  const [isPlaying, setIsPlaying] = useState(true);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const [hasVideoError, setHasVideoError] = useState(false);
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.volume = 0.6;
      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch(error => {
          // Browser blocked autoplay with audio. Mute it and play, show unmute prompt.
          debug('Autoplay with audio blocked, falling back to muted autoplay.');
          setIsMuted(true);
          setAutoplayBlocked(true);
          if (videoRef.current) {
            videoRef.current.muted = true;
            videoRef.current.play().catch(e => logError('Even muted autoplay failed', e));
          }
        });
      }
    }
  }, []);
  const toggleMute = () => {
    if (videoRef.current) {
      const newMutedState = !isMuted;
      videoRef.current.muted = newMutedState;
      setIsMuted(newMutedState);
      if (autoplayBlocked && !newMutedState) {
        setAutoplayBlocked(false);
      }
    }
  };
  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };
  return <div className="min-h-screen bg-[#FFF9F2] flex flex-col items-center justify-center p-4 sm:p-8 relative overflow-hidden font-sans" data-id="element-580" data-mp="lusakgzgqcon">
      {/* Subtle Ambient Background */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-581" data-mp="vsg5b6x2jk6t">
        <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] rounded-full bg-orange-100/50 blur-[120px]" data-id="element-582" data-mp="82canp1uyury" />
        <div className="absolute bottom-[-20%] right-[-10%] w-[60%] h-[60%] rounded-full bg-amber-100/50 blur-[120px]" data-id="element-583" data-mp="34ahucemrh3a" />
      </div>

      <div className="flex flex-col lg:flex-row items-center justify-center gap-12 lg:gap-20 w-full max-w-7xl relative z-10" data-id="element-584" data-mp="gwgwfu24xg0m">
        {/* Left Column: Content & CTAs */}
        <div className="w-full max-w-lg flex flex-col items-center lg:items-start text-center lg:text-left" data-id="element-585" data-mp="3kj7lq7f5vu4n">
          <motion.div className="mb-8 lg:mb-10" initial={{
          opacity: 0,
          y: -20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          ease: 'easeOut'
        }} data-id="element-586" data-mp="3ghma0ped5lw1">
            
            {/* The BrandLockup is now the singular hero brand element */}
            <BrandLockup size="xl" className="drop-shadow-md" data-id="element-587" data-mp="2we6lwek16b5r" />
          </motion.div>

          <motion.div className="mb-10 relative" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2,
          ease: 'easeOut'
        }} data-id="element-588" data-mp="31fnku4iyfnjp">
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-slate-900 tracking-tight leading-[1.1] mb-5" data-id="element-589" data-mp="3f5zei7v6tucy">
              Leren wordt een{' '}
              <span className="text-orange-500" data-id="element-590" data-mp="23rvjcycv606b">groot avontuur!</span>
            </h1>
            <p className="text-lg sm:text-xl text-slate-600 font-medium max-w-md mx-auto lg:mx-0 leading-relaxed" data-id="element-591" data-mp="28qozkyjjc5a6">
              Oefen rekenen, taal, spelling en meer — helemaal op jouw eigen
              niveau met Meester Djay.
            </p>
          </motion.div>

          <motion.div className="w-full space-y-4 max-w-md" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.4,
          ease: 'easeOut'
        }} data-id="element-592" data-mp="2k5tnapp47iwb">
            
            <Button variant="primary" size="xl" className="w-full text-lg shadow-lg hover:shadow-xl transition-shadow" onClick={onRegister} data-id="element-593" data-mp="37w77ju1gkdgc">
              
              Start je avontuur
              <ArrowRight className="w-6 h-6 ml-2" data-id="element-594" data-mp="2xx86awj0vk3d" />
            </Button>

            {/* De demoknop is hier weggehaald: demo-accounts gaan uit het
                 product. Ze schreven rechtstreeks naar de lokale accountlijst en
                 zouden daarmee dezelfde spookaccounts opleveren als het
                 adminformulier en de rosterimport — een account zonder profielrij
                 en dus zonder enige waarborg uit de database. */}
            <Button variant="outline" size="lg" className="w-full bg-white/80 backdrop-blur-sm border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors" onClick={onLogin} data-id="element-595" data-mp="212ac1u3hxdm">
              
              Log in
            </Button>
          </motion.div>

          <motion.div className="mt-8" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.6,
          delay: 0.6
        }} data-id="element-596" data-mp="bsg5jxf0kb0y">
            
            {/* Hier stond "100% Veilig & lokaal opgeslagen".
                 Dat is sinds de omzetting naar Supabase feitelijk onjuist: een
                 account staat op onze server, en met ouderlijke toestemming ook
                 de voortgang. Een onjuiste privacybelofte is erger dan geen
                 belofte — het is precies het soort claim waarop een
                 toezichthouder afrekent, en een ouder die het verschil later
                 ontdekt, vertrouwt de rest ook niet meer.
                  In plaats daarvan: wat er écht geldt, in één regel, met de
                 juridische stukken op klikafstand. */
          }
            <div className="space-y-2" data-id="element-597" data-mp="2yf44piit3qt4">
              {/* Ook "oefenen kan zonder account" was niet waar: je hebt een
                   account nodig, en dat staat op onze server. Wat er wél
                   verandert door toestemming is niet óf we iets opslaan maar
                   wát — beoordelingen (toetsuitslagen, rapporten) blijven zonder
                   toestemming op het apparaat. Dat is het eerlijke onderscheid,
                   en het is ook het enige dat een ouder echt moet weten. */}
              <p className="text-sm text-slate-500 font-medium flex items-center justify-center lg:justify-start gap-2" data-id="element-598" data-mp="3c7ae8lrgezjl">
                <Lock className="w-4 h-4 text-emerald-500 shrink-0" data-id="element-599" data-mp="3r3rm330fpw1e" />
                <span data-id="element-600" data-mp="3m5hqirasstf9">
                  Je maakt een account aan. Toetsresultaten bewaren we pas na
                  toestemming van je ouder.
                </span>
              </p>
              <p className="text-xs text-slate-400 flex flex-wrap items-center justify-center lg:justify-start gap-x-3 gap-y-1" data-id="element-601" data-mp="dmxqjkhfitzg">
                <a href="/privacy" className="underline decoration-slate-300 hover:text-slate-600 hover:decoration-slate-500 transition-colors" data-id="element-602" data-mp="3vrixu4534jpu">
                  
                  Privacyverklaring
                </a>
                <a href="/voorwaarden" className="underline decoration-slate-300 hover:text-slate-600 hover:decoration-slate-500 transition-colors" data-id="element-603" data-mp="3m04s3emjino9">
                  
                  Voorwaarden
                </a>
                <span className="text-slate-300" data-id="element-604" data-mp="3i0nl2grgtigz">
                  Voor kinderen, ouders en scholen
                </span>
              </p>
            </div>
          </motion.div>
        </div>

        {/* Right Column: Video */}
        <motion.div className="w-full max-w-md lg:max-w-lg relative" initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.8,
        delay: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }} data-id="element-605" data-mp="382pydxp97tqh">
          
          {/* Video Player Container - Cleaned up, no extra mascots */}
          <div className="relative rounded-[2rem] overflow-hidden shadow-2xl border-4 border-white bg-orange-50 group ring-1 ring-slate-900/5" data-id="element-606" data-mp="39kow6jxyhmdz">
            {!hasVideoError ? <video ref={videoRef} src="/gemini_generated_video_ffdfec60.mp4" loop playsInline className="w-full h-auto object-cover aspect-[9/16] sm:aspect-auto" onClick={togglePlay} onError={() => {
            debug('Video failed to load, falling back to illustration.');
            setHasVideoError(true);
          }} data-id="element-607" data-mp="34n6i6f530gz4" /> : <div className="w-full aspect-[9/16] sm:aspect-video bg-gradient-to-br from-orange-50 to-amber-100 flex flex-col items-center justify-center p-8" data-id="element-608" data-mp="2qs461a9n5vdv">
                <motion.div animate={{
              y: [0, -10, 0]
            }} transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-609" data-mp="2gx2igiv0l1de">
                
                  <DjayNarrator size="xl" emotion="excited" animate={true} data-id="element-610" data-mp="2bpm921iaqp18" />
                </motion.div>
                <div className="mt-6 text-center" data-id="element-611" data-mp="2gr1tbrcngrik">
                  <h3 className="text-xl font-bold text-orange-900 mb-2" data-id="element-612" data-mp="2lpwmnn88tygx">
                    Klaar voor de start!
                  </h3>
                  <p className="text-orange-800/80 font-medium" data-id="element-613" data-mp="1o362h39rhttp">
                    Jouw leeravontuur begint hier.
                  </p>
                </div>
              </div>}

            {/* Video Controls Overlay */}
            {!hasVideoError && <div className="absolute bottom-4 right-4 z-30 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 focus-within:opacity-100" data-id="element-614" data-mp="22qipkwf7w7m7">
                <button onClick={togglePlay} className="bg-black/60 hover:bg-black/80 text-white p-2.5 rounded-full backdrop-blur-md transition-colors focus:outline-none focus:ring-2 focus:ring-white/50" aria-label={isPlaying ? 'Pause' : 'Play'} data-id="element-615" data-mp="15av2bh6hs9za">
                
                  {isPlaying ? <Pause className="w-5 h-5" data-id="element-616" data-mp="3ffvn1qljmw2k" /> : <Play className="w-5 h-5 ml-0.5" data-id="element-617" data-mp="3fj9pszy4d7aj" />}
                </button>
                <button onClick={toggleMute} className="bg-black/60 hover:bg-black/80 text-white p-2.5 rounded-full backdrop-blur-md transition-colors focus:outline-none focus:ring-2 focus:ring-white/50" aria-label={isMuted ? 'Unmute' : 'Mute'} data-id="element-618" data-mp="3kgrrq6dpym91">
                
                  {isMuted ? <VolumeX className="w-5 h-5" data-id="element-619" data-mp="2wpw0bad6dt2b" /> : <Volume2 className="w-5 h-5" data-id="element-620" data-mp="2wqxg1s54z3qd" />}
                </button>
              </div>}

            {/* Autoplay Blocked Overlay */}
            {autoplayBlocked && <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/30 backdrop-blur-sm" data-id="element-621" data-mp="35j93d54yeb24">
                <button onClick={toggleMute} className="bg-white text-slate-900 hover:bg-orange-50 px-6 py-3 rounded-full font-bold flex items-center gap-2 shadow-xl transform transition hover:scale-105 focus:outline-none focus:ring-4 focus:ring-orange-500/50" data-id="element-622" data-mp="290biwvwo73v5">
                
                  <Volume2 className="w-5 h-5 text-orange-500" data-id="element-623" data-mp="20juedb46kkni" />
                  Zet geluid aan
                </button>
              </div>}
          </div>
        </motion.div>
      </div>
    </div>;
}